/**
 * USR-F1-T5 (review R1) — when a failed POST /auth/login/select-context has
 * already spent the context-selection token.
 *
 * The server spends the token (one UPDATE on its row in
 * context_selection_tokens) as the FIRST decision after the signature check,
 * before liveness, the forced password change, the sysadmin TOTP step and the
 * membership check. So every refusal the handler gives after that point —
 * TOTP_REQUIRED, TOTP_INVALID, NOT_SYSADMIN, NOT_A_MEMBER, TENANT_DELETED,
 * TENANT_SUSPENDED, CONTEXT_TOKEN_INVALID, CONTEXT_TOKEN_USED, and any 5xx
 * after the spend — leaves the picker holding a dead token. Posting it again
 * can only answer CONTEXT_TOKEN_USED and file a false "replayed" LOGIN_FAILED
 * audit row against an honest user.
 *
 * Three answers never reach the handler, so never spend the token — the
 * statuses the API really gives for them (SEC-F1-R3-03):
 *   - 422 — the body fails the route's schema (VALIDATION_ERROR,
 *           api plugins/error-handler.ts), e.g. a TOTP code that is not six
 *           digits;
 *   - 400 — a body Fastify cannot parse at all (not JSON);
 *   - 429 — the rate limiter (RATE_LIMITED), which answers before the body
 *           is even read.
 * The person keeps the picker, the token and what they typed, and can retry.
 * Every other failure sends the person back to the credentials step. The
 * rule is by status, not a list of codes, so a refusal added to the handler
 * later is covered without touching the web (nothing fails open).
 */
const NEVER_REACHED_THE_SPEND: ReadonlySet<number> = new Set([400, 422, 429]);

export function selectContextFailureSpentToken(status: number): boolean {
  return !NEVER_REACHED_THE_SPEND.has(status);
}

/**
 * The copy shown on the credentials step after a spent-token reset: the
 * server's reason, plus the way forward when the reason does not already
 * give it ("Authenticator code is invalid." alone invites a retry that can
 * never succeed).
 */
export function spentTokenMessage(message: string): string {
  return /(log|sign) in again\.?$/i.test(message.trim())
    ? message
    : `${message.trim()} Please sign in again.`;
}
